// The per-page head block of the pre-rendered HTML (ADR-0036): between <!--seo--> and <!--/seo--> in index.html.
import type { BreadcrumbList, Graph, Offer, Organization, SoftwareApplication, Thing, WebSite } from "schema-dts";
import { t } from "./i18n";
import { landingByPath, plainText, type PublicRoute } from "./landing";
import { PRICING_PUBLIC } from "./flags";
import { priceText } from "./price";

export const SITE = "https://marketing-kit.app";
const IMAGE = `${SITE}/brand/valas-512.png`;
/** The product name and its second name (ADR-0038). */
export const BRAND = "V-Kit";
export const BRAND_ALT = "Marketing Kit";
/** The tagline T of ADR-0038: the SoftwareApplication description. */
export const APP_DESCRIPTION = "V-Kit · Marketing Kit: your ad, app and subscription numbers - in Claude and ChatGPT";

export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE}/` : `${SITE}${path}`;
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** JSON for a <script type="application/ld+json">: `<` is escaped so the text can never close the tag. */
export function jsonLd(data: unknown): string {
  return `<script type="application/ld+json">${JSON.stringify(data).replace(/</g, "\\u003c")}</script>`;
}

const ORG_ID = "https://valas.team/#organization";

/** One paid period as a schema.org `Offer` (ADR-0045): the price per month or per year, VAT included (BL-177), as a
 *  `UnitPriceSpecification` billed in steps of one UN/CEFACT unit (MON, ANN); the card-less trial is in the description. */
function paidOffer(name: string, price: string, unitCode: "MON" | "ANN"): Offer {
  return {
    "@type": "Offer",
    name,
    price,
    priceCurrency: "EUR",
    description: priceText("long"),
    url: `${SITE}/pricing`,
    availability: "https://schema.org/InStock",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price,
      priceCurrency: "EUR",
      valueAddedTaxIncluded: true,
      billingIncrement: 1,
      unitCode,
    },
  };
}

/**
 * The `offers` of the SoftwareApplication - the one place; change it together with `price.*` in en.ts.
 * MK_PRICING_PUBLIC=on: two Offers, 19.99 EUR a month and 199 EUR a year (ADR-0045); off: the 0 EUR Offer of
 * ADR-0044 (ADR-0048). The static copy in index.html is rewritten from it at build (scripts/prerender.mjs).
 */
export const OFFER: Offer | Offer[] = PRICING_PUBLIC
  ? [paidOffer(t("price.monthly"), "19.99", "MON"), paidOffer(t("price.yearly"), "199", "ANN")]
  : {
      "@type": "Offer",
      name: priceText("short"),
      price: "0",
      priceCurrency: "EUR",
      description: priceText("long"),
      availability: "https://schema.org/InStock",
    };

const ORGANIZATION: Organization = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Valas Team",
  url: "https://valas.team/",
  logo: IMAGE,
  sameAs: ["https://www.linkedin.com/company/111395213/", "https://valas.team/", "https://github.com/arey325/marketing-kit-plugin"],
};

const WEBSITE: WebSite = {
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  name: BRAND,
  alternateName: BRAND_ALT,
  url: `${SITE}/`,
  publisher: { "@id": ORG_ID },
};

/** The JSON-LD graph of a page: Organization, WebSite, SoftwareApplication with offers, BreadcrumbList when the page has one. */
export function graphFor(url: string, crumb?: string, parent?: { name: string; item: string }): Graph {
  const app: SoftwareApplication = {
    "@type": "SoftwareApplication",
    name: BRAND,
    alternateName: BRAND_ALT,
    url,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: APP_DESCRIPTION,
    publisher: { "@id": ORG_ID },
    offers: OFFER,
  };
  const graph: Thing[] = [ORGANIZATION, WEBSITE, app];
  if (crumb) {
    const list: BreadcrumbList = {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        ...(parent ? [{ "@type": "ListItem" as const, position: 2, name: parent.name, item: parent.item }] : []),
        { "@type": "ListItem", position: parent ? 3 : 2, name: crumb, item: url },
      ],
    };
    graph.push(list);
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export function headBlock(route: PublicRoute): string {
  const url = canonicalUrl(route.path);
  const landing = landingByPath(route.path);
  const parentPage = landing?.parent ? landingByPath(landing.parent) : undefined;
  const meta = [
    `<title>${escapeHtml(route.title)}</title>`,
    `<meta name="description" content="${escapeHtml(route.description)}" />`,
    `<link rel="canonical" href="${url}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${BRAND}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:title" content="${escapeHtml(route.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(route.description)}" />`,
    `<meta property="og:image" content="${IMAGE}" />`,
    `<meta name="twitter:card" content="summary" />`,
    jsonLd(graphFor(url, route.breadcrumb ?? landing?.breadcrumb, parentPage && { name: parentPage.breadcrumb, item: canonicalUrl(parentPage.path) })),
  ];
  // Built but unlisted (MK_PRICING_PUBLIC=off, ADR-0048): the page opens, search engines leave it out.
  if (route.unlisted) meta.splice(2, 0, `<meta name="robots" content="noindex" />`);
  const faq = landing?.faq ?? route.faq;
  if (faq?.length) {
    meta.push(
      jsonLd({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: plainText(f.a) },
        })),
      }),
    );
  }
  return meta.map((l) => `    ${l}`).join("\n");
}
